<template>
  <div class="upcoming-meetings-app tw-bg-gradient-to-r tw-from-purple-100 tw-via-purple-50 tw-to-pink-100 tw-min-h-screen tw-p-8">
    <div class="tw-max-w-4xl tw-mx-auto">
      <div
        class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6 tw-mb-4"
      >
        <InviteRecall :entity-external-id="entityExternalId" />
      </div>

      <!-- File Upload -->
      <div class="columns">
        <div class="column is-half">
          <div class="box">
            <h2 class="title is-5 mb-2">
              Automatic Meeting Import
            </h2>
            <p class="mb-4">
              Connect your accounts to automatically import meetings:
            </p>

            <ZoomWrapper />
          </div>
        </div><!-- column-->
        <div class="column is-half">
          <div class="box">
            <h2 class="title is-5 mb-2">
              Manually Create Meeting
            </h2>
            <p class="mb-4">
              Upload a recording or create a meeting manually:
            </p>
            <div class="file has-name is-fullwidth is-small">
              <label class="file-label">
                <input
                  class="file-input"
                  type="file"
                  accept="video/mp4,audio/mpeg"
                  @change="handleFileUpload"
                >
                <span class="file-cta">
                  <span class="file-icon">
                    <i class="fas fa-upload" />
                  </span>
                  <span class="file-label">
                    Choose a file…
                  </span>
                </span>
                <span class="file-name">
                  {{ selectedFile ? selectedFile.name : 'No file selected' }}
                </span>
              </label>
            </div>
            <button
              class="button is-primary mt-2 is-small is-fullwidth"
              :class="{ 'is-loading': isUploading }"
              :disabled="isUploading || !selectedFile"
              @click="uploadFile"
            >
              Upload
            </button>
          </div>
        </div><!-- column-->
      </div><!-- columns-->

      <!-- Meetings List -->
      <div class="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-6">
        <h2 class="tw-text-xl tw-font-semibold tw-mb-4 tw-text-gray-800">
          Your Meetings
        </h2>
        <div
          v-if="meetings.length === 0"
          class="tw-text-gray-600"
        >
          No meetings found.
        </div>
        <div
          v-else
          class="tw-space-y-4"
        >
          <div
            v-for="meeting in meetings"
            :key="meeting.id"
            :class="['tw-rounded-lg tw-p-4 tw-shadow tw-max-w-2xl tw-mx-auto',
                     meeting.summary ? 'tw-bg-gray-100' : 'tw-bg-white']"
          >
            <div class="tw-flex tw-justify-between tw-items-start">
              <div>
                <h3 :class="['tw-text-lg tw-font-semibold', meeting.summary ? 'tw-text-gray-500' : 'tw-text-gray-800']">
                  {{ meeting.title || 'Untitled Meeting' }}
                </h3>
                <p class="tw-text-sm tw-text-gray-500">
                  {{ formatDate(meeting.meeting_date) }}
                </p>
              </div>
              <div class="tw-flex tw-flex-col tw-items-end tw-gap-3">
                <MeetingStatus
                  :status="meeting.status"
                  :failure-reason="meeting.failure_reason || 'We were unable to process this meeting'"
                />

                <div class="tw-flex tw-gap-3">
                  <router-link
                    v-if="meeting.summary"
                    :to="{
                      name: 'entity-note-taking',
                      params: { entityExternalId: entityExternalId },
                      query: { meetingId: meeting.id }
                    }"
                    class="tw-text-blue-500"
                    @click.native="$route.query.meetingId === meeting.id && $router.go()"
                  >
                    <i
                      class="fa fa-eye tw-text-sky-600 hover:tw-text-sky-700"
                      title="View summary"
                    />
                  </router-link>

                  <DeleteMeeting
                    :meeting="meeting"
                    @deleted="fetchMeetings"
                  >
                    <i
                      class="fa fa-trash tw-text-rose-600 hover:tw-text-rose-700 tw-cursor-pointer"
                      title="Delete meeting"
                    />
                  </DeleteMeeting>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { awaitTask } from '../../checkup/helpers/tasks';
import StakeModal from '@stakeui/StakeModal.vue';
import ZoomWrapper from '../../meetings/components/ZoomWrapper.vue';
import InviteRecall from '@frontend/ui/features/meetings/components/InviteRecall.vue';
import MeetingStatus from '@frontend/ui/features/meetings/components/MeetingStatus.vue';
import DeleteMeeting from '@frontend/ui/features/meetings/components/DeleteMeeting.vue';

import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

export default {
  components: {
    StakeModal,
    ZoomWrapper,
    InviteRecall,
    MeetingStatus,
    DeleteMeeting,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      meetings: [],
      selectedFile: null,
      showZoomMeetingModal: false,
      pollingInterval: null, // Add this line
      isUploading: false, // Add this line
    };
  },
  computed: {
    ...mapStores(useAdvisorStore),
  },
  mounted() {
    this.fetchMeetings();
    this.startPolling(); // Add this line
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    handleFileUpload(event) {
      this.selectedFile = event.target.files[0];
    },
    async uploadFile() {
      if (!this.selectedFile) {
        alert('Please select a file first');
        return;
      }

      this.isUploading = true;

      try {
        // Create a new FormData instance
        const formData = new FormData();

        // Convert file to base64 before sending
        const base64File = await this.fileToBase64(this.selectedFile);
        const sanitizedFile = new File(
            [base64File],
            this.selectedFile.name,
            { type: this.selectedFile.type },
        );

        formData.append('file', sanitizedFile);
        formData.append('entity_external_id', this.entityExternalId);

        const response = await awaitTask('/api/v1/meetings/upload/', {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
          },
        });

        if (response.task.status === 'complete') {
          // Find the pending meeting and update it
          const meetingIndex = this.meetings.findIndex(meeting => meeting.id === response.task.meta.meeting_id);
          if (meetingIndex !== -1) {
            this.meetings[meetingIndex] = {
              ...this.meetings[meetingIndex],
              title: 'New Meeting',
              date: new Date(),
              status: 'completed',
            };
          } else {
            // If not found, add as a new meeting
            this.meetings.unshift({
              id: response.task.meta.meeting_id,
              title: 'New Meeting',
              date: new Date(),
              status: 'completed',
            });
          }
        } else {
          console.error('File upload failed:', response);
          alert('File upload failed. Please try again.');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('An error occurred while uploading the file. Please try again.');
      } finally {
        this.isUploading = false;
      }
    },
    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    formatDate(date) {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(date);
    },
    async fetchMeetings() {
      try {
        const response = await fetch(`/api/v1/meetings/entity/${this.entityExternalId}/`);
        const data = await response.json();
        this.updateMeetings(data.meetings);
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    },
    updateMeetings(newMeetings) {
      this.meetings = newMeetings.map(meeting => ({
        id: meeting.id,
        status: meeting.status,
        failure_reason: meeting.failure_reason,
        title: meeting.summary ? this.generateTitle(meeting.summary) : 'New Meeting',
        meeting_date: new Date(meeting.meeting_date),
        summary: meeting.summary,
        entity_external_id: this.entityExternalId, // Add this line
      }));
    },
    startPolling() {
      this.pollingInterval = setInterval(() => {
        this.fetchMeetings();
      }, 10000); // Poll every 10 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    },
    generateTitle(summary) {
      // Generate a title from the summary (e.g., first 5 words)
      return summary.split(' ').slice(0, 5).join(' ') + '...';
    },
  },
};
</script>

<style scoped>
/* You can add any additional custom styles here if needed */
</style>
